import 'monitorToolsSetup'
import { ErrorBoundary } from '@neo/blocks/forms/boundary'
import { Toaster } from '@neo/primitives/toaster'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'router-v6'
import { CompatApp } from 'v2/compat/compat-app'
import { queryClient } from 'v2/configs/rq-setup'
import { HubspotTracker } from 'v2/shared/components/hubspot-tracker'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import { AuthProvider } from 'v2/shared/hooks/use-auth'
import { FeatureFlagsProvider } from 'v2/shared/hooks/use-feature-flags'
import { IntegrationRolloutProvider } from 'v2/shared/hooks/use-integration-rollout'
import { PubNubProvider } from 'v2/shared/hooks/use-pubnub'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { getLocalAuth } from 'v2/utils'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { idbPersister } from 'v2/utils/idb-query-persister'
import './index.scss'
import { UpdateHandler } from 'v2/shared/components/update-handler'
import { PermissionsProvider } from 'v2/shared/hooks/use-permissions'

// Initializing a global object to store global data/utils
window.cx = {}

const root = ReactDOM.createRoot(document.getElementById('root'))

const app = (
  <>
    <BrowserRouter>
      {/* Outside AuthProvider/CompatApp: HubSpot has to see the anonymous
          signup + login routes, and CompatApp withholds the whole tree while
          the query cache is restoring. Never inside a partner's embed iframe. */}
      {!isEmbedMode() && <HubspotTracker />}
      <AuthProvider>
        <PubNubProvider>
          <FeatureFlagsProvider>
            <IntegrationRolloutProvider>
              <PermissionsProvider>
                <CompatApp />
              </PermissionsProvider>
            </IntegrationRolloutProvider>
          </FeatureFlagsProvider>
        </PubNubProvider>
      </AuthProvider>
    </BrowserRouter>
    <Toaster />
    <ReactQueryDevtools initialIsOpen={false} />
  </>
)

root.render(
  <ErrorBoundary>
    <Suspense fallback={<SuspenseLoader />}>
      {isEmbedMode() ? (
        // Embed never persists its query cache: the buster below is '' in the iframe, so every
        // embed session would restore another's agents and flags from IndexedDB.
        <QueryClientProvider client={queryClient}>{app}</QueryClientProvider>
      ) : (
        <PersistQueryClientProvider
          client={queryClient}
          // buster = active org's aom; on a post-switch reload it won't match the
          // persisted cache's tag, so the whole cache is discarded on restore and
          // no other org's data can rehydrate. Same-org reloads keep the cache.
          persistOptions={{ persister: idbPersister, buster: getLocalAuth().aom_uuid || '' }}
          onSuccess={() => {
            primaryEventBus.emit(PrimaryEvents.RQ_HYDRATED)
          }}
        >
          {app}
        </PersistQueryClientProvider>
      )}
    </Suspense>
    {/* No service worker (and so no update prompt) in embed. */}
    {!isEmbedMode() && <UpdateHandler />}
  </ErrorBoundary>,
)

// If you want your app to work offline and load faster, you can change
// unregister() to signup() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
